import { useEffect, useMemo, useState } from "react"
import { getMyCourseTestApprovals, getMyCourseContentApprovals, updateCourseTestApproval, updateCourseContentApproval } from "@/api/kuizy"
import DataTable from '@/widgets/DataTable'
import { ClipboardList, FileText, Search, UserCheck } from "lucide-react"
import { motion } from "framer-motion"
import Select from "react-select"
import ExamDetailApproval from "@/widgets/ExamDetailApproval"

const statusOptions = [
  { label: "รออนุมัติ", value: "0" },
  { label: "อนุมัติแล้ว", value: "1" },
  { label: "ปฏิเสธ", value: "2" },
  { label: "ทั้งหมด", value: "all" },
]

const statusBadge = (status) => {
  const map = { 0: "bg-yellow-200 text-yellow-800", 1: "bg-green-200 text-green-800", 2: "bg-red-200 text-red-800" }
  const label = { 0: "รออนุมัติ", 1: "อนุมัติแล้ว", 2: "ปฏิเสธ" }
  return <span className={`px-2 py-1 text-xs rounded ${map[status]}`}>{label[status]}</span>
}

// ─── Exam Approval Tab ───────────────────────────────────────────────────────
function ExamApprovalTab() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedTestId, setSelectedTestId] = useState(null)
  const [selectedCourseTestId, setSelectedCourseTestId] = useState(null)
  const [searchTxt, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("0")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [sortBy, setSortBy] = useState("requested_at")
  const [sortDir, setSortDir] = useState("desc")

  const fetchList = async () => {
    setLoading(true)
    try {
      const res = await getMyCourseTestApprovals({ status: statusFilter, search: searchTxt, page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir })
      setRows(res.data.rows)
      setTotal(res.data.total)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchList() }, [statusFilter, page, perPage, sortBy, sortDir])

  const handleAction = async (id, status) => { await updateCourseTestApproval(id, status); fetchList() }

  const columns = useMemo(() => [
    { key: "course_name", title: "หลักสูตร", sortable: true },
    {
      key: "test_name", title: "ชุดข้อสอบ", sortable: true,
      render: (row) => (
        <button onClick={(e) => { e.stopPropagation(); setSelectedTestId(row.test_id); setSelectedCourseTestId(row.course_test_id); setDetailVisible(true) }}
          className="text-primary hover:underline font-medium">{row.test_name}</button>
      )
    },
    { key: "requested_by_name", title: "ส่งคำขอโดย", sortable: true },
    { key: "requested_at", title: "ส่งคำขอเมื่อ", sortable: true, render: row => new Date(row.requested_at).toLocaleString() },
    { key: "approval_status", title: "สถานะ", sortable: true, render: row => statusBadge(row.approval_status) },
    { key: "approved_by_name", title: "ผู้อนุมัติ", render: row => row.approved_by_name || "-" },
    { key: "approved_at", title: "วันที่อนุมัติ", render: row => row.approved_at ? new Date(row.approved_at).toLocaleString() : "-" },
    {
      key: "action", title: "การอนุมัติ", pinEnd: true,
      render: row => (
        <div className="flex gap-2 justify-end">
          <button onClick={() => handleAction(row.course_test_id, 1)} className="px-3 py-1 text-xs bg-green-600 text-white rounded">อนุมัติ</button>
          <button onClick={() => handleAction(row.course_test_id, 2)} className="px-3 py-1 text-xs bg-red-600 text-white rounded">ปฏิเสธ</button>
        </div>
      )
    },
  ], [sortBy, sortDir])

  return (
    <>
      <FilterBar
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        searchTxt={searchTxt} setSearch={setSearch}
        setPage={setPage} onSearch={fetchList}
        placeholder="ค้นหาหลักสูตร / ชุดข้อสอบ"
      />
      <DataTable columns={columns} rows={rows} loading={loading}
        page={page} perPage={perPage} total={total}
        sortBy={sortBy} sortDir={sortDir}
        onSortChange={(k, d) => { setSortBy(k); setSortDir(d) }}
        onPageChange={setPage}
        onPerPageChange={(val) => { setPage(1); setPerPage(val) }}
        rowKey={(row) => row.course_test_id}
      />
      <ExamDetailApproval
        visible={detailVisible} testId={selectedTestId} courseTestId={selectedCourseTestId}
        onClose={() => { setDetailVisible(false); setSelectedTestId(null); setSelectedCourseTestId(null) }}
      />
    </>
  )
}

// ─── Content Approval Tab ────────────────────────────────────────────────────
function ContentApprovalTab() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTxt, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("0")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [sortBy, setSortBy] = useState("requested_at")
  const [sortDir, setSortDir] = useState("desc")

  const fetchList = async () => {
    setLoading(true)
    try {
      const res = await getMyCourseContentApprovals({ status: statusFilter, search: searchTxt, page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir })
      setRows(res.data?.rows || [])
      setTotal(res.data?.total || 0)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchList() }, [statusFilter, page, perPage, sortBy, sortDir])

  const handleAction = async (id, status) => { await updateCourseContentApproval(id, status); fetchList() }

  const columns = useMemo(() => [
    { key: "course_name", title: "หลักสูตร", sortable: true },
    { key: "content_name", title: "เนื้อหา", sortable: true, render: row => <span className="font-medium text-gray-800">{row.content_name}</span> },
    { key: "content_type", title: "ประเภท", sortable: true, render: row => row.content_type ? <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">{row.content_type}</span> : "-" },
    { key: "requested_by_name", title: "ส่งคำขอโดย", sortable: true },
    { key: "requested_at", title: "ส่งคำขอเมื่อ", sortable: true, render: row => new Date(row.requested_at).toLocaleString() },
    { key: "approval_status", title: "สถานะ", sortable: true, render: row => statusBadge(row.approval_status) },
    { key: "approved_by_name", title: "ผู้อนุมัติ", render: row => row.approved_by_name || "-" },
    { key: "approved_at", title: "วันที่อนุมัติ", render: row => row.approved_at ? new Date(row.approved_at).toLocaleString() : "-" },
    {
      key: "action", title: "การอนุมัติ", pinEnd: true,
      render: row => (
        <div className="flex gap-2 justify-end">
          <button onClick={() => handleAction(row.course_content_id, 1)} className="px-3 py-1 text-xs bg-green-600 text-white rounded">อนุมัติ</button>
          <button onClick={() => handleAction(row.course_content_id, 2)} className="px-3 py-1 text-xs bg-red-600 text-white rounded">ปฏิเสธ</button>
        </div>
      )
    },
  ], [sortBy, sortDir])

  return (
    <>
      <FilterBar
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        searchTxt={searchTxt} setSearch={setSearch}
        setPage={setPage} onSearch={fetchList}
        placeholder="ค้นหาหลักสูตร / เนื้อหา"
      />
      <DataTable columns={columns} rows={rows} loading={loading}
        page={page} perPage={perPage} total={total}
        sortBy={sortBy} sortDir={sortDir}
        onSortChange={(k, d) => { setSortBy(k); setSortDir(d) }}
        onPageChange={setPage}
        onPerPageChange={(val) => { setPage(1); setPerPage(val) }}
        rowKey={(row) => row.course_content_id}
      />
    </>
  )
}

// ─── Shared Filter Bar ───────────────────────────────────────────────────────
function FilterBar({ statusFilter, setStatusFilter, searchTxt, setSearch, setPage, onSearch, placeholder }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row sm:flex-wrap gap-4"
    >
      <div className="flex flex-col gap-1 w-full sm:w-48">
        <label className="flex items-center gap-1 text-sm"><UserCheck size={14} /> สถานะ</label>
        <Select classNamePrefix="rs" options={statusOptions}
          value={statusOptions.find(o => o.value === statusFilter)}
          onChange={o => { setStatusFilter(o.value); setPage(1) }}
        />
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
        <label className="flex items-center gap-1 text-sm"><Search size={14} /> ค้นหา</label>
        <div className="relative">
          <input placeholder={placeholder} value={searchTxt}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { setPage(1); onSearch() } }}
            className="border border-gray-300 rounded-lg px-10 py-2 w-full text-sm focus:ring-2 focus:ring-primary"
          />
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>
      <button onClick={() => { setPage(1); onSearch() }}
        className="h-[42px] self-end inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-4 text-sm whitespace-nowrap"
      >
        <Search size={15} /> ค้นหา
      </button>
    </motion.div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────
const TABS = [
  { key: "exam", label: "อนุมัติชุดข้อสอบ", icon: <ClipboardList size={15} /> },
  { key: "content", label: "อนุมัติเนื้อหา", icon: <FileText size={15} /> },
]

export default function ApprovalDashboard() {
  const [activeTab, setActiveTab] = useState("exam")

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
        <ClipboardList size={20} /> อนุมัติ
      </h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div key={activeTab}>
        {activeTab === 'exam' && <ExamApprovalTab />}
        {activeTab === 'content' && <ContentApprovalTab />}
      </div>
    </div>
  )
}